import React, { useCallback, useMemo } from 'react'
import { Row, Col, Popover } from 'antd'
import { Horizontal, Texto } from '@gravitate-js/excalibrr'
import { TripStatusIcon } from '@constants/TripStatusIcons'
import moment from 'moment'

import { ReadableDifference } from '../../../utils/general'
import { statusClassDict, translateStatus } from '../../../constants/orderDispatch'
import { CheckCircleFilled } from '@ant-design/icons'
import { useSecurityContext } from '@components/Navigation/SecurityContext'
import { formatters } from '@utils/intl/dateTimeFormatters'

export const DispatchStatusTag = ({ order, showTime = true, showTimezone = true, stackCarrierWithDriver }) => {
  const { scopes } = useSecurityContext()
  const isCarrierUser = scopes?.is_carrier
  const acceptedStatus = order.state === 'accepted'
  const isCrossroadsOrder = order.carrier_info?.client_goid
  const pendingDriverAssignment = order.state === 'assigned' && !order.driver
  const isCarrierOrder = order.carrier || order.manager
  const internalCrossroadsPendingDispatch = !isCarrierUser && isCrossroadsOrder && pendingDriverAssignment
  const assignedCarrier = order.carrier || order.manager

  const getDisplayTime = () => {
    if (!order.estimated_end) return null
    if (order.state === 'complete' && order?.status_detail?.updated) {
      return formatters.SHORT_DATE_TIME(moment(order.status_detail.updated))
    }
    if (order?.estimated_end) return formatters.SHORT_DATE_TIME(moment(order.estimated_end))
  }
  const orderNotifyState = useMemo(() => {
    switch (order?.carrier_notify_state) {
      case 'none':
        return 'Assigned'
      case 'failed':
        return 'Email Failed'
      default:
        return order?.carrier_notify_state || null
    }
  }, [order?.carrier_notify_state])

  const getStatusAppearance = useCallback((order) => {
    switch (order.carrier_notify_state) {
      case 'failed':
        return 'error'
      case 'sent':
        return 'secondary'
      default:
        return 'medium'
    }
  }, [])

  if (internalCrossroadsPendingDispatch) {
    return (
      <Col flex={1} className='pl-3  vertical-flex h-100'>
        <Texto className='text-ellipsis pr-2' weight='bold'>
          {assignedCarrier}
        </Texto>
        <Texto appearance='medium'>Pending Dispatch</Texto>
      </Col>
    )
  } else if (acceptedStatus) {
    return (
      <Col flex={1} className='pl-3  vertical-flex h-100'>
        <Texto appearance='medium'>Not Assigned</Texto>
      </Col>
    )
  } else if (order.state === 'canceled') {
    return (
      <Horizontal flex={1} className='pl-3'>
        <Texto appearance='medium'>Canceled</Texto>
      </Horizontal>
    )
  } else if ((isCarrierUser && isCarrierOrder) || (isCarrierOrder && !isCrossroadsOrder)) {
    return (
      <Col flex={1} className=' vertical-flex h-100'>
        <Texto className='text-ellipsis pr-2' weight='bold'>
          {assignedCarrier}
        </Texto>

        <div>
          {order.state === 'complete' ? (
            <>
              <Texto appearance='success' category='p1' weight='bold'>
                <CheckCircleFilled /> Completed
              </Texto>
            </>
          ) : (
            <Horizontal>
              <Texto className='pr-1' appearance='medium'>
                Status:
              </Texto>
              <Texto
                appearance={getStatusAppearance(order)}
                className='text-ellipsis'
                style={{ textTransform: 'capitalize' }}
                weight='bold'
              >
                {orderNotifyState}
              </Texto>
            </Horizontal>
          )}
        </div>
      </Col>
    )
  }
  const getStatusColor = (status) => {
    switch (status) {
      case 'complete':
        return 'success'
      case 'unset':
        return 'default'
      default:
        return 'primary'
    }
  }

  return (
    <Popover
      content={<DispatchStatusPopover order={order} showTimezone={showTimezone} />}
      title={<UpdateTitle />}
      trigger='hover'
    >
      <Row style={{ flexWrap: 'nowrap', alignItems: 'center', gap: 8 }}>
        {order.trip_status && (
          <Col
            className='vertical-flex-center status-icon-col'
            style={{ fontSize: 16, ...(!showTime && { width: 20, height: 20 }) }}
          >
            <Texto appearance={getStatusColor(order.trip_status)}>
              <TripStatusIcon status={order.trip_status} />
            </Texto>
          </Col>
        )}

        <Col flex={1} className='vertical-flex-center h-100'>
          <div style={{ fontWeight: 'normal' }}>
            <b> {order.driver}</b>
            {stackCarrierWithDriver && isCarrierOrder && (
              <b> {order.driver ? `(${assignedCarrier})` : assignedCarrier}</b>
            )}
            {showTime && (
              <div>
                {order.state !== 'complete' && 'ETA:'}
                {getDisplayTime()}
              </div>
            )}
          </div>
        </Col>
      </Row>
    </Popover>
  )
}
const DispatchStatusPopover = ({ order, showTimezone = false }) => {
  const currentStatusClass = statusClassDict[order.trip_status]
  const orderETA = order.estimated_end ? moment(order.estimated_end) : null
  const getStatusColor = (status) => {
    switch (status) {
      case 'complete':
        return 'success'
      case 'unset':
        return 'default'
      default:
        return 'primary'
    }
  }
  const orderUpdated = order?.status_detail?.updated
    ? formatters.DATE_TIME(
        moment(order?.status_detail?.updated),
        showTimezone ? order?.status_detail?.updated_local : undefined
      )
    : null
  return (
    <div style={{ minWidth: 300 }}>
      <Row>
        <Col flex={2}>
          <Texto category='label' weight='600' className='popover-subtitle'>
            Estimated Delivery Time
          </Texto>
          <div className='popover-icon' align='top'>
            <Texto category='p1' className='popover-status-text'>
              {orderETA
                ? formatters.SHORT_DATE_TIME(orderETA, showTimezone ? order.estimated_end_local : undefined)
                : null}
            </Texto>
          </div>
        </Col>
        <Col flex={1} align='right'>
          <Texto category='label' weight='600' className='popover-subtitle'>
            {order.state !== 'complete' ? 'ETA' : 'Order Completed'}
          </Texto>
          <div>
            <Texto category='p1' className='popover-status-text' align='right'>
              {order?.state !== 'complete' ? ReadableDifference(orderETA) : orderUpdated}
            </Texto>
          </div>
        </Col>
      </Row>
      {order.trip_status && (
        <Row>
          <Col flex={2}>
            <Texto category='label' weight='600' className='popover-subtitle'>
              Current Status
            </Texto>
            <Horizontal gap={8} verticalCenter className='popover-icon' align='top'>
              <Texto
                weight='600'
                style={{ display: 'flex', alignItems: 'center', gap: 4 }}
                category='p1'
                appearance={getStatusColor(order.trip_status)}
                className={`popover-status-text `}
              >
                <TripStatusIcon status={order.trip_status} />
                {translateStatus[order.trip_status]}
              </Texto>
            </Horizontal>
          </Col>
          <Col flex={1} align='right'>
            <Texto category='label' weight='600' className='popover-subtitle'>
              At:
            </Texto>
            <div>
              <p align='right' className='popover-city-text'>
                {order?.location_name}
              </p>
            </div>
          </Col>
        </Row>
      )}
    </div>
  )
}
const UpdateTitle = () => {
  return (
    <Row align='middle'>
      <Col flex={2}>
        <div className='popover-title'>Driver Status</div>
      </Col>
    </Row>
  )
}
